import type { StreamFrame, TrafficEvent } from '@broker/contracts';
import { API_URL } from '../api/client.js';

/**
 * The live traffic, held outside React so every page can read the same stream without each one
 * opening its own connection. One socket per console, whatever is on screen.
 *
 * Only the last `CAPACITY` frames are kept: this is a window on what is happening now, and the
 * history lives in Broker, where it belongs.
 */

const CAPACITY = 300;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

export type ConnectionState = 'connecting' | 'live' | 'offline';

interface TrafficSnapshot {
  events: TrafficEvent[];
  sequence: number;
  connection: ConnectionState;
}

let snapshot: TrafficSnapshot = { events: [], sequence: 0, connection: 'connecting' };
const listeners = new Set<() => void>();
let socket: WebSocket | undefined;
let reconnectAt = RECONNECT_MIN_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let subscribers = 0;

const emit = () => {
  for (const listener of listeners) listener();
};

const update = (patch: Partial<TrafficSnapshot>) => {
  snapshot = { ...snapshot, ...patch };
  emit();
};

function push(event: TrafficEvent): void {
  const events = [event, ...snapshot.events];
  snapshot = {
    events: events.length > CAPACITY ? events.slice(0, CAPACITY) : events,
    sequence: Math.max(snapshot.sequence, event.sequence),
    connection: 'live',
  };
  emit();
}

function handle(frame: StreamFrame): void {
  if (frame.type === 'hello') update({ sequence: frame.sequence, connection: 'live' });
  else if (frame.type === 'traffic') push(frame.event);
}

function connect(): void {
  if (socket || subscribers === 0) return;
  update({ connection: 'connecting' });

  const url = new URL(`${API_URL}/ws`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('sequence', String(snapshot.sequence));

  const next = new WebSocket(url.toString());
  socket = next;

  next.addEventListener('message', (message) => {
    try {
      handle(JSON.parse(String(message.data)) as StreamFrame);
    } catch {
      // A frame we cannot read is not a reason to drop the connection.
    }
  });
  next.addEventListener('open', () => {
    reconnectAt = RECONNECT_MIN_MS;
    update({ connection: 'live' });
  });
  const dropped = () => {
    if (socket !== next) return;
    socket = undefined;
    update({ connection: 'offline' });
    if (subscribers === 0) return;
    // Backing off matters: a broker that is down must not be hit by every open console at once.
    reconnectTimer = setTimeout(connect, reconnectAt);
    reconnectAt = Math.min(reconnectAt * 2, RECONNECT_MAX_MS);
  };
  next.addEventListener('close', dropped);
  next.addEventListener('error', dropped);
}

export const trafficStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    subscribers += 1;
    if (subscribers === 1) connect();
    return () => {
      listeners.delete(listener);
      subscribers -= 1;
      if (subscribers > 0) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
      socket = undefined;
    };
  },
  getSnapshot: (): TrafficSnapshot => snapshot,
  /** Used by the Dashboard to align the window with the position the overview was taken at. */
  seed(sequence: number): void {
    if (sequence > snapshot.sequence) update({ sequence });
  },
  clear(): void {
    update({ events: [] });
  },
};
